import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Switch,
} from '@mui/material';
import { Search as SearchIcon, TrendingDown, TrendingUp, Warning, Close } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import Layout from '../components/Layout';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

/* ───────────────── types ───────────────── */
interface SubstitutionRow {
  id?: string;
  pivot_month: string;
  comm_lvl: string;
  country_a_iso: string;
  country_a_name: string;
  country_a_pre_avg: number | null;
  country_a_post_avg: number | null;
  country_a_drop: number | null;
  country_a_pct_drop: number | null;
  country_b_iso: string;
  country_b_name: string;
  country_b_pre_avg: number | null;
  country_b_post_avg: number | null;
  country_b_spike: number | null;
  country_b_pct_spike: number | null;
  country_b_first_import: string | null;
  hts_code: string;
  hts_description: string;
  substitution_rate: number | null;
  pattern_type: string;
  risk_level: string;
}

interface HtsShiftRow {
  id?: string;
  pivot_month: string;
  comm_lvl: string;
  hts_code: string;
  hts_description: string;
  total_drop_value: number | null;
  total_spike_value: number | null;
  net_change: number | null;
  top_losers: any[];
  top_gainers: any[];
  has_new_supplier: boolean;
  new_supplier_countries: string[];
  has_huge_growth: boolean;
  huge_growth_countries: string[];
  loser_count: number;
  gainer_count: number;
  new_supplier_count: number;
  investigation_priority: string;
}

interface SummaryData {
  pivot_month: string;
  comm_lvl: string;
  months_window: number;
  total_patterns: number;
  high_risk_count: number;
  medium_risk_count: number;
  low_risk_count: number;
  new_supplier_count: number;
  huge_growth_count: number;
  growth_count: number;
  total_drop_value: number | null;
  total_spike_value: number | null;
  net_substitution: number | null;
  countries_with_drops: number;
  countries_with_spikes: number;
  unique_hts_codes: number;
  top_loser_countries: any[];
  top_gainer_countries: any[];
  top_affected_hts: any[];
}

interface FiltersData {
  pivot_months: string[];
  commodity_levels: string[];
  countries?: {
    losers: { iso: string; name: string; count: number }[];
    gainers: { iso: string; name: string; count: number }[];
  };
}

interface MonthlyDataPoint {
  time_period: string;
  iso_code: string;
  cty_name: string;
  ves_val_mo: number | null;
  is_first_import: boolean;
  is_pre_pivot: boolean;
  is_counted?: boolean;
}

interface ChartCountry {
  iso: string;
  name: string;
}

interface SubstitutionDetail {
  pivot_month: string;
  hts_code: string;
  country_a: {
    iso: string;
    name: string | null;
    monthly_data: MonthlyDataPoint[];
  };
  country_b: {
    iso: string;
    name: string | null;
    monthly_data: MonthlyDataPoint[];
  };
  summary: {
    country_a_pre_avg: number | null;
    country_a_post_avg: number | null;
    country_a_drop: number | null;
    country_a_pct_drop: number | null;
    country_b_pre_avg: number | null;
    country_b_post_avg: number | null;
    country_b_spike: number | null;
    country_b_pct_spike: number | null;
    country_b_first_import: string | null;
    substitution_rate: number | null;
    pattern_type: string;
    risk_level: string;
    hts_description: string;
  } | null;
  months_window: number;
  chart_data?: Record<string, any>[];
  chart_countries?: ChartCountry[];
}

interface HtsSearchResult {
  hts_code: string;
  hts_description: string;
  comm_lvl: string;
  total_import_value: number | null;
  avg_monthly_value: number | null;
  country_count: number;
  top_countries: any[];
}

interface HtsShiftCountryData {
  iso: string;
  name: string;
  pre_avg: number | null;
  post_avg: number | null;
  change: number | null;
  pct_change: number | null;
  direction: 'DROP' | 'SPIKE';
  is_new_supplier: boolean;
  monthly_data: MonthlyDataPoint[];
}

interface HtsShiftDetail {
  pivot_month: string;
  hts_code: string;
  hts_description: string;
  months_window: number;
  countries: HtsShiftCountryData[];
  chart_data: Record<string, any>[];
  chart_countries: ChartCountry[];
}

/* ───────────────── helpers ───────────────── */
const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const absVal = Math.abs(val);
  if (absVal >= 1e9) return `$${(absVal / 1e9).toFixed(1)}B`;
  if (absVal >= 1e6) return `$${(absVal / 1e6).toFixed(1)}M`;
  if (absVal >= 1e3) return `$${(absVal / 1e3).toFixed(0)}K`;
  return `$${absVal.toLocaleString()}`;
};

const formatPercent = (val: number | null | undefined): string => {
  if (val == null) return '-';
  return `${val.toFixed(1)}%`;
};

const formatMonthLabel = (pivotMonth: string): string => {
  const [year, month] = pivotMonth.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const getRiskChipColor = (risk: string): 'error' | 'warning' | 'success' | 'default' => {
  switch (risk?.toUpperCase()) {
    case 'HIGH': return 'error';
    case 'MEDIUM': return 'warning';
    case 'LOW': return 'success';
    default: return 'default';
  }
};

const getPatternChipColor = (pattern: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (pattern) {
    case 'NEW_SUPPLIER': return 'error';
    case 'HUGE_GROWTH': return 'warning';
    case 'GROWTH': return 'info';
    default: return 'default';
  }
};

const dataGridStyles = {
  backgroundColor: 'white',
  borderRadius: 0,
  border: '1px solid',
  borderColor: 'divider',
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: 'action.hover',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    paddingTop: '4px',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 8px',
    fontSize: '0.9375rem',
  },
  '& .MuiDataGrid-cell:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-cell:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#e0e0e0',
    borderRadius: 0,
    borderBottom: '3px solid #9e9e9e',
    maxHeight: 'none !important',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#e0e0e0',
    padding: '12px 8px',
    height: 'auto !important',
    maxHeight: 'none !important',
    alignItems: 'flex-start',
  },
  '& .MuiDataGrid-columnHeader:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-columnHeader:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    overflow: 'visible',
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    overflow: 'visible',
    whiteSpace: 'normal',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'text.primary',
    fontWeight: 700,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1.3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    textOverflow: 'unset',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: '#fafafa',
  },
};

/* ───────────────── component ───────────────── */
export default function TariffScheme() {
  const { access } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  // State
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState<FiltersData | null>(null);
  const [pivotMonth, setPivotMonth] = useState<string>('');
  const [commodityLevel, _setCommodityLevel] = useState<string>('HS10');
  const [countryA, setCountryA] = useState<string>('');
  const [countryB, setCountryB] = useState<string>('');
  const [patternType, setPatternType] = useState<string>('');
  const [riskLevel, setRiskLevel] = useState<string>('');
  const [htsSearch, setHtsSearch] = useState<string>('');
  const [transientSpike, setTransientSpike] = useState<boolean>(true);
  const [minSpikeMonths, setMinSpikeMonths] = useState<number | null>(null);
  const [trueNewSupplier, setTrueNewSupplier] = useState<boolean>(false);
  const [excludeOilGas, setExcludeOilGas] = useState<boolean>(false);


  // View state
  const [view, setView] = useState<'summary' | 'substitutions' | 'hts-shifts'>('substitutions');

  // Data
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [substitutions, setSubstitutions] = useState<SubstitutionRow[]>([]);
  const [htsShifts, setHtsShifts] = useState<HtsShiftRow[]>([]);

  // Pagination - separate state per tab to avoid cross-tab interference
  const [subsPagination, setSubsPagination] = useState({ page: 0, pageSize: 50 });
  const [htsPagination, setHtsPagination] = useState({ page: 0, pageSize: 50 });
  const [subsRowCount, setSubsRowCount] = useState(0);
  const [htsRowCount, setHtsRowCount] = useState(0);

  // Sorting (only for substitutions)
  const [subsSortModel, setSubsSortModel] = useState<GridSortModel>([]);

  // HTS Search
  const [htsOptions, setHtsOptions] = useState<HtsSearchResult[]>([]);
  const [htsSearchLoading, setHtsSearchLoading] = useState(false);
  const [htsInputValue, setHtsInputValue] = useState('');
  const [selectedHts, setSelectedHts] = useState<HtsSearchResult | null>(null);

  // HTS Shifts detail panel
  const [selectedHtsShift, setSelectedHtsShift] = useState<HtsShiftRow | null>(null);
  const [htsShiftDetail, setHtsShiftDetail] = useState<HtsShiftDetail | null>(null);
  const [htsShiftDetailLoading, setHtsShiftDetailLoading] = useState(false);
  const [selectedHtsCountry, setSelectedHtsCountry] = useState<string | null>(null);

  // Substitution detail panel
  const [selectedSubstitution, setSelectedSubstitution] = useState<SubstitutionRow | null>(null);
  const [substitutionDetail, setSubstitutionDetail] = useState<SubstitutionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showOnlySelectedCountries, setShowOnlySelectedCountries] = useState(false);

  // Fetch scheme ID
  useEffect(() => {
    const fetchScheme = async () => {
      try {
        const res = await api.get('/schemes/', { headers });
        const schemes = res.data.results ?? res.data;
        const tariffScheme = schemes.find((s: any) => s.code === 'tariff-coo-fraud');
        if (tariffScheme) {
          setSchemeId(tariffScheme.id);
        } else {
          setError('Tariff scheme not found');
        }
      } catch (err) {
        setError('Failed to load scheme');
        console.error(err);
      }
    };
    fetchScheme();
  }, [headers]);

  // Fetch filters (re-fetch when pivotMonth changes to update country options)
  useEffect(() => {
    if (!schemeId) return;
    const fetchFilters = async () => {
      try {
        const params: any = { commodity_level: commodityLevel };
        if (pivotMonth && pivotMonth !== 'all') {
          params.pivot_month = pivotMonth;
        }
        const res = await api.get(`/schemes/${schemeId}/tariff/filters/`, { headers, params });
        setFilters(res.data);
        // Only set default pivotMonth if not already set (empty string = initial state)
        if (res.data.pivot_months?.length > 0 && !pivotMonth) {
          // Default to Liberation Day (2025-04) if available, otherwise first month
          const liberationDay = res.data.pivot_months.find((m: string) => m === '2025-04');
          setPivotMonth(liberationDay || res.data.pivot_months[0]);
        }
      } catch (err) {
        console.error('Failed to load filters:', err);
      }
    };
    fetchFilters();
  }, [schemeId, pivotMonth, commodityLevel, headers, view]);

  // Fetch summary (only when a specific pivot month is selected)
  useEffect(() => {
    if (!schemeId || !pivotMonth || pivotMonth === 'all') {
      if (pivotMonth === 'all') setSummary(null);
      return;
    }
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/schemes/${schemeId}/tariff/summary/`, {
          headers,
          params: { pivot_month: pivotMonth, commodity_level: commodityLevel },
        });
        setSummary(res.data);
      } catch (err) {
        console.error('Failed to load summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [schemeId, pivotMonth, commodityLevel, headers]);

  // Fetch substitutions - driven by useEffect, not called directly
  useEffect(() => {
    if (!schemeId || !pivotMonth || view !== 'substitutions') return;

    const fetchData = async () => {
      setTableLoading(true);
      try {
        const params: any = {
          commodity_level: commodityLevel,
          page: subsPagination.page + 1, // API is 1-indexed
          page_size: subsPagination.pageSize,
        };
        if (pivotMonth !== 'all') params.pivot_month = pivotMonth;
        if (countryA) params.country_a = countryA;
        if (countryB) params.country_b = countryB;
        if (patternType) params.pattern_type = patternType;
        if (riskLevel) params.risk_level = riskLevel;
        if (htsSearch) params.hts_code = htsSearch;
        if (transientSpike) params.transient_spike = 'true';
        if (transientSpike && minSpikeMonths) params.min_spike_months = minSpikeMonths;
        if (trueNewSupplier) params.true_new_supplier = 'true';
        if (excludeOilGas) params.exclude_oil_gas = 'true';
        if (subsSortModel.length > 0) {
          params.sort_field = subsSortModel[0].field;
          params.sort_order = subsSortModel[0].sort || 'desc';
        }

        const res = await api.get(`/schemes/${schemeId}/tariff/substitutions/`, { headers, params });
        // Add unique IDs including page to avoid collisions across pages
        const rowsWithIds = res.data.results.map((row: SubstitutionRow, idx: number) => ({
          ...row,
          id: `${subsPagination.page}-${row.pivot_month}-${row.country_a_iso}-${row.country_b_iso}-${row.hts_code}-${idx}`,
        }));
        setSubstitutions(rowsWithIds);
        setSubsRowCount(res.data.total);
      } catch (err) {
        console.error('Failed to load substitutions:', err);
      } finally {
        setTableLoading(false);
      }
    };

    fetchData();
  }, [schemeId, view, pivotMonth, commodityLevel, countryA, countryB, patternType, riskLevel, htsSearch, transientSpike, minSpikeMonths, trueNewSupplier, excludeOilGas, subsPagination, subsSortModel, headers]);

  // Fetch HTS shifts - driven by useEffect, not called directly
  useEffect(() => {
    if (!schemeId || !pivotMonth || view !== 'hts-shifts') return;

    const fetchData = async () => {
      setTableLoading(true);
      try {
        const params: any = {
          commodity_level: commodityLevel,
          page: htsPagination.page + 1, // API is 1-indexed
          page_size: htsPagination.pageSize,
        };
        if (pivotMonth !== 'all') params.pivot_month = pivotMonth;
        if (htsSearch) params.hts_code = htsSearch;
        if (excludeOilGas) params.exclude_oil_gas = 'true';

        const res = await api.get(`/schemes/${schemeId}/tariff/hts-shifts/`, { headers, params });
        // Add unique IDs including page to avoid collisions
        const rowsWithIds = res.data.results.map((row: HtsShiftRow, idx: number) => ({
          ...row,
          id: `${htsPagination.page}-${row.pivot_month}-${row.hts_code}-${idx}`,
        }));
        setHtsShifts(rowsWithIds);
        setHtsRowCount(res.data.total);
      } catch (err) {
        console.error('Failed to load HTS shifts:', err);
      } finally {
        setTableLoading(false);
      }
    };

    fetchData();
  }, [schemeId, view, pivotMonth, commodityLevel, htsSearch, excludeOilGas, htsPagination, headers]);

  // Reset pagination to page 0 when filters change (but not when pagination itself changes)
  const prevSubsFiltersRef = useRef<string | null>(null);
  useEffect(() => {
    const filterKey = JSON.stringify({ pivotMonth, commodityLevel, countryA, countryB, patternType, riskLevel, htsSearch, transientSpike, minSpikeMonths, trueNewSupplier, excludeOilGas, subsSortModel });
    if (prevSubsFiltersRef.current !== null && prevSubsFiltersRef.current !== filterKey) {
      setSubsPagination(prev => ({ ...prev, page: 0 }));
    }
    prevSubsFiltersRef.current = filterKey;
  }, [pivotMonth, commodityLevel, countryA, countryB, patternType, riskLevel, htsSearch, transientSpike, minSpikeMonths, trueNewSupplier, excludeOilGas, subsSortModel]);

  const prevHtsFiltersRef = useRef<string | null>(null);
  useEffect(() => {
    const filterKey = JSON.stringify({ pivotMonth, commodityLevel, htsSearch, excludeOilGas });
    if (prevHtsFiltersRef.current !== null && prevHtsFiltersRef.current !== filterKey) {
      setHtsPagination(prev => ({ ...prev, page: 0 }));
    }
    prevHtsFiltersRef.current = filterKey;
  }, [pivotMonth, commodityLevel, htsSearch, excludeOilGas]);

  // Reset country filters when pivot month changes
  useEffect(() => {
    setCountryA('');
    setCountryB('');
  }, [pivotMonth]);

  // Fetch HTS search results with debounce
  useEffect(() => {
    if (!schemeId || htsInputValue.length < 2) {
      setHtsOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setHtsSearchLoading(true);
      try {
        const res = await api.get(`/schemes/${schemeId}/tariff/hts-search/`, {
          headers,
          params: { q: htsInputValue, commodity_level: commodityLevel, limit: 20 },
        });
        setHtsOptions(res.data.results || []);
      } catch (err) {
        console.error('Failed to search HTS codes:', err);
        setHtsOptions([]);
      } finally {
        setHtsSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [schemeId, htsInputValue, commodityLevel, headers]);

  // Fetch substitution detail when row is clicked
  const fetchSubstitutionDetail = useCallback(async (row: SubstitutionRow) => {
    if (!schemeId) return;
    setDetailLoading(true);
    setSelectedSubstitution(row);
    try {
      const res = await api.get(`/schemes/${schemeId}/tariff/substitution-detail/`, {
        headers,
        params: {
          pivot_month: row.pivot_month,
          hts_code: row.hts_code,
          country_a: row.country_a_iso,
          country_b: row.country_b_iso,
          commodity_level: row.comm_lvl,
        },
      });
      setSubstitutionDetail(res.data);
    } catch (err) {
      console.error('Failed to load substitution detail:', err);
      setSubstitutionDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [schemeId, headers]);

  // Fetch HTS shift detail when row is clicked
  const fetchHtsShiftDetail = useCallback(async (row: HtsShiftRow) => {
    if (!schemeId) return;
    setHtsShiftDetailLoading(true);
    setSelectedHtsShift(row);
    setSelectedHtsCountry(null);
    setHtsShiftDetail(null);
    try {
      const res = await api.get(`/schemes/${schemeId}/tariff/hts-shift-detail/`, {
        headers,
        params: {
          pivot_month: row.pivot_month,
          hts_code: row.hts_code,
          commodity_level: row.comm_lvl,
        },
      });
      setHtsShiftDetail(res.data);
      // Auto-select first country if available
      if (res.data.countries?.length > 0) {
        setSelectedHtsCountry(res.data.countries[0].iso);
      }
    } catch (err) {
      console.error('Failed to load HTS shift detail:', err);
      setHtsShiftDetail(null);
    } finally {
      setHtsShiftDetailLoading(false);
    }
  }, [schemeId, headers]);

  /* ───────────────── columns ───────────────── */
  const substitutionsCols: GridColDef[] = [
    {
      field: 'pivot_month',
      headerName: 'Pivot',
      width: 100,
      renderHeader: () => (
        <Tooltip title="The pivot month when tariff changes took effect" placement="top">
          <span style={{ fontWeight: 700 }}>Pivot</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontSize: '0.85rem' }}>
          {params.value ? formatMonthLabel(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'country_a_name',
      headerName: 'Losing Country',
      flex: 1,
      minWidth: 140,
      renderHeader: () => (
        <Tooltip title="Country that saw decreased imports after the pivot month" placement="top">
          <span style={{ fontWeight: 700 }}>Losing Country</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
          {params.value}
        </Box>
      ),
    },
    {
      field: 'country_a_drop',
      headerName: 'Drop',
      width: 110,
      renderHeader: () => (
        <Tooltip title="Average monthly decrease in imports. Calculated as (post-pivot avg) - (pre-pivot avg) over 3-month windows." placement="top">
          <span style={{ fontWeight: 700 }}>Drop</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'error.main', fontWeight: 500 }}>
          -{formatMoney(params.value)}
        </Typography>
      ),
    },
    {
      field: 'country_b_name',
      headerName: 'Gaining Country',
      flex: 1,
      minWidth: 140,
      renderHeader: () => (
        <Tooltip title="Country that saw increased imports after the pivot month" placement="top">
          <span style={{ fontWeight: 700 }}>Gaining Country</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
          {params.value}
        </Box>
      ),
    },
    {
      field: 'country_b_spike',
      headerName: 'Spike',
      width: 110,
      renderHeader: () => (
        <Tooltip title="Average monthly increase in imports. Calculated as (post-pivot avg) - (pre-pivot avg) over 3-month windows." placement="top">
          <span style={{ fontWeight: 700 }}>Spike</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'success.main', fontWeight: 500 }}>
          +{formatMoney(params.value)}
        </Typography>
      ),
    },
    {
      field: 'hts_code',
      headerName: 'HTS Code',
      width: 100,
      renderHeader: () => (
        <Tooltip title="Harmonized Tariff Schedule code identifying the product category" placement="top">
          <span style={{ fontWeight: 700 }}>HTS Code</span>
        </Tooltip>
      ),
    },
    {
      field: 'hts_description',
      headerName: 'Product',
      flex: 2,
      minWidth: 200,
      renderHeader: () => (
        <Tooltip title="Product description from the HTS schedule" placement="top">
          <span style={{ fontWeight: 700 }}>Product</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''}>
          <Typography noWrap sx={{ fontSize: '0.85rem' }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'substitution_rate',
      headerName: 'Subst. Rate',
      width: 100,
      renderHeader: () => (
        <Tooltip title="How much of the losing country's drop was absorbed by the gaining country. Calculated as: Spike ÷ |Drop|. 100% = full substitution." placement="top">
          <span style={{ fontWeight: 700 }}>Subst. Rate</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => formatPercent(params.value ? params.value * 100 : null),
    },
  ];

  const htsShiftsCols: GridColDef[] = [
    {
      field: 'hts_code',
      headerName: 'HTS Code',
      width: 100,
      renderHeader: () => (
        <Tooltip title="Harmonized Tariff Schedule code identifying the product category" placement="top">
          <span style={{ fontWeight: 700 }}>HTS Code</span>
        </Tooltip>
      ),
    },
    {
      field: 'hts_description',
      headerName: 'Product',
      flex: 1,
      minWidth: 180,
      renderHeader: () => (
        <Tooltip title="Product description from the HTS schedule" placement="top">
          <span style={{ fontWeight: 700 }}>Product</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''}>
          <Typography noWrap sx={{ fontSize: '0.85rem' }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'total_drop_value',
      headerName: 'Total Drop',
      width: 120,
      renderHeader: () => (
        <Tooltip title="Sum of all import decreases across losing countries for this HTS code" placement="top">
          <span style={{ fontWeight: 700 }}>Total Drop</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'error.main' }}>
          -{formatMoney(params.value)}
        </Typography>
      ),
    },
    {
      field: 'total_spike_value',
      headerName: 'Total Spike',
      width: 120,
      renderHeader: () => (
        <Tooltip title="Sum of all import increases across gaining countries for this HTS code" placement="top">
          <span style={{ fontWeight: 700 }}>Total Spike</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ color: 'success.main' }}>
          +{formatMoney(params.value)}
        </Typography>
      ),
    },
    {
      field: 'loser_count',
      headerName: 'Losers',
      width: 80,
      align: 'center',
      renderHeader: () => (
        <Tooltip title="Number of countries with decreased imports for this HTS code" placement="top">
          <span style={{ fontWeight: 700 }}>Losers</span>
        </Tooltip>
      ),
    },
    {
      field: 'gainer_count',
      headerName: 'Gainers',
      width: 80,
      align: 'center',
      renderHeader: () => (
        <Tooltip title="Number of countries with increased imports for this HTS code" placement="top">
          <span style={{ fontWeight: 700 }}>Gainers</span>
        </Tooltip>
      ),
    },
    {
      field: 'has_new_supplier',
      headerName: 'New Supplier',
      width: 130,
      renderHeader: () => (
        <Tooltip title="Whether any gaining country had little/no imports before the pivot month" placement="top">
          <span style={{ fontWeight: 700 }}>New Supplier</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label="Yes" size="small" color="error" icon={<Warning />} />
        ) : (
          <Typography color="text.secondary">-</Typography>
        ),
    },
    {
      field: 'new_supplier_count',
      headerName: '# New',
      width: 85,
      align: 'center',
      renderHeader: () => (
        <Tooltip title="Number of new supplier countries for this HTS code" placement="top">
          <span style={{ fontWeight: 700 }}># New</span>
        </Tooltip>
      ),
    },
  ];

  /* ───────────────── render ───────────────── */
  if (error) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <Box
        mb={4}
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Box p={3} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Tariffs: Country of Origin Fraud
              </Typography>
              <Box sx={{ opacity: 0.8, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" component="span">
                  Analyze import substitution patterns to detect fraudulent COO declarations
                </Typography>
                {pivotMonth && (
                  <Chip
                    label={pivotMonth === 'all' ? 'All Pivot Months' : formatMonthLabel(pivotMonth)}
                    size="small"
                    sx={{ ml: 1, backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
                  />
                )}
              </Box>
            </Box>
            {summary && (
              <Box display="flex" gap={4} alignItems="center">
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Typography variant="body2" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    Patterns
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.total_patterns.toLocaleString()}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Typography variant="body2" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    High Risk
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.high_risk_count.toLocaleString()}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Typography variant="body2" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    Trade Drop
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatMoney(summary.total_drop_value)}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Typography variant="body2" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    Substitution
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatMoney(summary.total_spike_value)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Main Content Area with Sidebar */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Left Sidebar */}
          <Box
            sx={{
              width: 200,
              flexShrink: 0,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              height: 'fit-content',
            }}
          >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Scope Section */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                    Scope
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ mt: 0.25 }}>
                    <InputLabel sx={{ fontSize: '0.8rem' }}>Pivot Month</InputLabel>
                    <Select
                      value={pivotMonth || 'all'}
                      label="Pivot Month"
                      onChange={(e) => setPivotMonth(e.target.value)}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>All Pivot Months</MenuItem>
                      {filters?.pivot_months?.map((m) => (
                        <MenuItem key={m} value={m} sx={{ fontSize: '0.8rem' }}>
                          {formatMonthLabel(m)}
                          {m === '2025-04' && ' ★'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Divider sx={{ my: 0.5 }} />

                {/* View Toggle */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                    View
                  </Typography>
                  <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, v) => {
                      if (!v) return;
                      setView(v);
                    }}
                    size="small"
                    fullWidth
                    sx={{ mt: 0.25 }}
                  >
                    <ToggleButton value="substitutions" sx={{ fontSize: '0.6rem', py: 0.25, px: 0.5 }}>Subs</ToggleButton>
                    <ToggleButton value="hts-shifts" sx={{ fontSize: '0.6rem', py: 0.25, px: 0.5 }}>HTS</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* HTS Search */}
                {(view === 'substitutions' || view === 'hts-shifts') && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Product
                    </Typography>
                    <Autocomplete
                      size="small"
                      fullWidth
                      freeSolo
                      options={htsOptions}
                      loading={htsSearchLoading}
                      value={selectedHts}
                      inputValue={htsInputValue}
                      onInputChange={(_, value) => {
                        setHtsInputValue(value);
                        if (!value) {
                          setHtsSearch('');
                          setSelectedHts(null);
                        }
                      }}
                      onChange={(_, value) => {
                        if (typeof value === 'string') {
                          setHtsSearch(value);
                          setSelectedHts(null);
                        } else if (value) {
                          setSelectedHts(value);
                          setHtsSearch(value.hts_code);
                        } else {
                          setSelectedHts(null);
                          setHtsSearch('');
                        }
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.hts_code;
                      }}
                      isOptionEqualToValue={(option, value) => option.hts_code === value.hts_code}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.hts_code}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {option.hts_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, fontSize: '0.65rem' }}>
                              {option.hts_description}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search HTS..."
                          sx={{ mt: 0.25, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: '1rem' }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {htsSearchLoading ? <CircularProgress color="inherit" size={14} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                )}

                {/* Substitutions Filters */}
                {view === 'substitutions' && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                        Filters
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.25 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Losing Country</InputLabel>
                          <Select
                            value={countryA}
                            label="Losing Country"
                            onChange={(e) => setCountryA(e.target.value)}
                            sx={{ fontSize: '0.8rem' }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                          >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All</MenuItem>
                            {filters?.countries?.losers?.map((c) => (
                              <MenuItem key={c.iso} value={c.iso} sx={{ fontSize: '0.8rem' }}>
                                {c.name} ({c.count})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Gaining Country</InputLabel>
                          <Select
                            value={countryB}
                            label="Gaining Country"
                            onChange={(e) => setCountryB(e.target.value)}
                            sx={{ fontSize: '0.8rem' }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                          >
                            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All</MenuItem>
                            {filters?.countries?.gainers?.map((c) => (
                              <MenuItem key={c.iso} value={c.iso} sx={{ fontSize: '0.8rem' }}>
                                {c.name} ({c.count})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 0.5 }} />

                    {/* Signals Section */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                        Signals
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                        <Tooltip
                          title="Spike > 5× historical, then returned to baseline"
                          placement="right"
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={transientSpike}
                                onChange={(e) => setTransientSpike(e.target.checked)}
                                size="small"
                                color="warning"
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: transientSpike ? 600 : 400 }}>
                                Transient Spike
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: '100%',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: transientSpike ? 'warning.50' : 'transparent',
                              border: '1px solid',
                              borderColor: transientSpike ? 'warning.main' : 'divider',
                            }}
                          />
                        </Tooltip>

                        {/* Min spike months - show when transient enabled */}
                        {transientSpike && (
                          <FormControl size="small" fullWidth sx={{ pl: 1.5 }}>
                            <Select
                              value={minSpikeMonths || ''}
                              onChange={(e) => setMinSpikeMonths(e.target.value ? Number(e.target.value) : null)}
                              displayEmpty
                              sx={{ fontSize: '0.75rem' }}
                            >
                              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Any # months</MenuItem>
                              <MenuItem value={2} sx={{ fontSize: '0.75rem' }}>2+ months</MenuItem>
                              <MenuItem value={3} sx={{ fontSize: '0.75rem' }}>All 3 months</MenuItem>
                            </Select>
                          </FormControl>
                        )}

                        {/* New Supplier */}
                        <Tooltip
                              title="Never imported this product before pivot month"
                              placement="right"
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={trueNewSupplier}
                                    onChange={(e) => setTrueNewSupplier(e.target.checked)}
                                    size="small"
                                    color="success"
                                  />
                                }
                                label={
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: trueNewSupplier ? 600 : 400 }}>
                                    New Supplier
                                  </Typography>
                                }
                                sx={{
                                  ml: 0,
                                  width: '100%',
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: trueNewSupplier ? 'success.50' : 'transparent',
                                  border: '1px solid',
                                  borderColor: trueNewSupplier ? 'success.main' : 'divider',
                                }}
                              />
                        </Tooltip>
                      </Box>
                    </Box>

                  </>
                )}

                {/* Exclusions Section - shown for both substitutions and HTS views */}
                {(view === 'substitutions' || view === 'hts-shifts') && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase' }}>
                        Exclusions
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                        <Tooltip
                          title="Exclude HTS Chapter 27 (Mineral fuels, oils, petroleum products)"
                          placement="right"
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={excludeOilGas}
                                onChange={(e) => setExcludeOilGas(e.target.checked)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: excludeOilGas ? 600 : 400 }}>
                                Exclude Oil/Gas
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: '100%',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: excludeOilGas ? 'grey.100' : 'transparent',
                              border: '1px solid',
                              borderColor: excludeOilGas ? 'grey.400' : 'divider',
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
          </Box>

          {/* Right Content Area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Summary Dashboard */}
        {view === 'summary' && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : summary ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Summary Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Total Patterns
                      </Typography>
                      <Typography variant="h4">{summary.total_patterns.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        High Risk
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'error.main' }}>
                        {summary.high_risk_count.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        New Suppliers
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'warning.main' }}>
                        {summary.new_supplier_count.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Total Trade Drop
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'error.main' }}>
                        {formatMoney(summary.total_drop_value)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Total Substitution
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'success.main' }}>
                        {formatMoney(summary.total_spike_value)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Unique HTS Codes
                      </Typography>
                      <Typography variant="h4">{summary.unique_hts_codes.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* Pattern Breakdown */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Pattern Breakdown
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          By Risk Level
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip label={`High: ${summary.high_risk_count}`} color="error" size="small" />
                          <Chip label={`Medium: ${summary.medium_risk_count}`} color="warning" size="small" />
                          <Chip label={`Low: ${summary.low_risk_count}`} color="success" size="small" />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          By Pattern Type
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip
                            label={`New Supplier: ${summary.new_supplier_count}`}
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={`Huge Growth: ${summary.huge_growth_count}`}
                            color="warning"
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={`Growth: ${summary.growth_count}`}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPatternType('NEW_SUPPLIER');
                      setRiskLevel('HIGH');
                      setView('substitutions');
                    }}
                  >
                    View High-Risk New Suppliers
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setCountryA('CN');
                      setView('substitutions');
                    }}
                  >
                    View China Trade Shifts
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography color="text.secondary">No data available for selected filters.</Typography>
            )}
          </Box>
        )}

        {/* Substitutions Table */}
        {view === 'substitutions' && (
          <Box sx={{ height: 'calc(100vh - 230px)', minHeight: 400 }}>
            <DataGrid
              rows={substitutions}
              columns={substitutionsCols}
              loading={tableLoading}
              rowCount={subsRowCount}
              paginationMode="server"
              paginationModel={subsPagination}
              onPaginationModelChange={(model) => {
                // Only update if actually different (prevents DataGrid clamp loops)
                if (model.page !== subsPagination.page || model.pageSize !== subsPagination.pageSize) {
                  setSubsPagination(model);
                }
              }}
              sortingMode="server"
              sortModel={subsSortModel}
              onSortModelChange={(model: GridSortModel) => {
                // Apply "desc first" logic for value columns
                if (model.length > 0) {
                  const field = model[0].field;
                  let sort = model[0].sort || 'desc';
                  const descFirstFields = ['country_a_drop', 'country_b_spike', 'substitution_rate', 'total_drop_value', 'total_spike_value'];
                  if (descFirstFields.includes(field) && sort === 'asc' && (subsSortModel.length === 0 || subsSortModel[0].field !== field)) {
                    sort = 'desc';
                  }
                  setSubsSortModel([{ field, sort }]);
                } else {
                  setSubsSortModel([]);
                }
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              disableColumnMenu
              onRowClick={(params) => fetchSubstitutionDetail(params.row as SubstitutionRow)}
              sx={dataGridStyles}
            />
          </Box>
        )}

        {/* HTS Shifts Table */}
        {view === 'hts-shifts' && (
          <Box sx={{ height: 'calc(100vh - 230px)', minHeight: 400 }}>
            <DataGrid
              rows={htsShifts}
              columns={htsShiftsCols}
              loading={tableLoading}
              rowCount={htsRowCount}
              paginationMode="server"
              paginationModel={htsPagination}
              onPaginationModelChange={(model) => {
                // Only update if actually different (prevents DataGrid clamp loops)
                if (model.page !== htsPagination.page || model.pageSize !== htsPagination.pageSize) {
                  setHtsPagination(model);
                }
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              disableColumnMenu
              onRowClick={(params) => fetchHtsShiftDetail(params.row as HtsShiftRow)}
              sx={dataGridStyles}
            />
          </Box>
        )}

        {/* HTS Shift Detail Dialog */}
        <Dialog
          open={!!selectedHtsShift}
          onClose={() => {
            setSelectedHtsShift(null);
            setHtsShiftDetail(null);
            setSelectedHtsCountry(null);
          }}
          maxWidth="xl"
          fullWidth
          disableScrollLock
          disableRestoreFocus
          sx={{ '& .MuiDialog-paper': { height: '85vh' } }}
        >
          {selectedHtsShift && (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                    {selectedHtsShift.hts_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedHtsShift.hts_description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 3, mr: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Total Drop</Typography>
                      <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
                        -{formatMoney(selectedHtsShift.total_drop_value)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Total Spike</Typography>
                      <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                        +{formatMoney(selectedHtsShift.total_spike_value)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Priority</Typography>
                      <Box>
                        <Chip
                          label={selectedHtsShift.investigation_priority}
                          size="small"
                          color={getRiskChipColor(selectedHtsShift.investigation_priority)}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <IconButton onClick={() => { setSelectedHtsShift(null); setHtsShiftDetail(null); setSelectedHtsCountry(null); }} size="small">
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {htsShiftDetailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : htsShiftDetail ? (
                  <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
                    {/* Left Panel - Country List */}
                    <Box sx={{
                      width: 280,
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      overflowY: 'auto',
                      flexShrink: 0,
                    }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: '#fafafa' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Countries ({htsShiftDetail.countries.length})
                        </Typography>
                      </Box>
                      {htsShiftDetail.countries.map((country) => (
                        <Box
                          key={country.iso}
                          onClick={() => setSelectedHtsCountry(country.iso)}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: selectedHtsCountry === country.iso ? 'action.selected' : 'transparent',
                            '&:hover': { backgroundColor: selectedHtsCountry === country.iso ? 'action.selected' : 'action.hover' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {country.direction === 'DROP' ? (
                              <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
                            ) : (
                              <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                              {country.name}
                            </Typography>
                            {country.is_new_supplier && (
                              <Warning sx={{ color: 'error.main', fontSize: 14 }} />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              {country.direction === 'DROP' ? 'Drop:' : 'Spike:'}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                color: country.direction === 'DROP' ? 'error.main' : 'success.main',
                              }}
                            >
                              {country.direction === 'DROP' ? '-' : '+'}{formatMoney(Math.abs(country.change || 0))}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              Pre → Post:
                            </Typography>
                            <Typography variant="caption">
                              {formatMoney(country.pre_avg)} → {formatMoney(country.post_avg)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {/* Right Panel - Selected Country Detail */}
                    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
                      {selectedHtsCountry ? (() => {
                        const country = htsShiftDetail.countries.find(c => c.iso === selectedHtsCountry);
                        if (!country) return null;
                        return (
                          <>
                            {/* Country Header */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {country.direction === 'DROP' ? (
                                  <TrendingDown sx={{ color: 'error.main' }} />
                                ) : (
                                  <TrendingUp sx={{ color: 'success.main' }} />
                                )}
                                <Typography variant="h6">
                                  {country.name}
                                </Typography>
                                {country.is_new_supplier && (
                                  <Chip label="New Supplier" size="small" color="error" />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Pre-Pivot Avg</Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {formatMoney(country.pre_avg)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Post-Pivot Avg</Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {formatMoney(country.post_avg)}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {country.direction === 'DROP' ? 'Drop' : 'Spike'}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 500,
                                      color: country.direction === 'DROP' ? 'error.main' : 'success.main',
                                    }}
                                  >
                                    {country.direction === 'DROP' ? '-' : '+'}{formatMoney(Math.abs(country.change || 0))}
                                    {country.pct_change && ` (${country.direction === 'DROP' ? '-' : '+'}${Math.abs(country.pct_change).toFixed(0)}%)`}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Monthly Data Table */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              Monthly Import Values
                            </Typography>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', mb: 3 }}>
                              <Box component="thead">
                                <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                                  <Box component="th" sx={{ textAlign: 'left', py: 1, pr: 2 }}>Month</Box>
                                  <Box component="th" sx={{ textAlign: 'right', py: 1 }}>Value</Box>
                                </Box>
                              </Box>
                              <Box component="tbody">
                                {country.monthly_data.map((d, idx) => {
                                  const isCounted = d.is_counted !== false;
                                  return (
                                    <Box
                                      component="tr"
                                      key={idx}
                                      sx={{
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: !isCounted ? '#f5f5f5' : 'transparent',
                                        ...(d.time_period === htsShiftDetail.pivot_month && {
                                          borderTop: '2px dashed',
                                          borderTopColor: 'text.secondary',
                                        }),
                                      }}
                                    >
                                      <Box component="td" sx={{ py: 1, pr: 2, color: isCounted ? 'text.primary' : 'text.disabled' }}>
                                        {formatMonthLabel(d.time_period)}
                                        {d.time_period === htsShiftDetail.pivot_month && (
                                          <Chip label="Pivot" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                                        )}
                                        {d.is_first_import && (
                                          <Chip label="First Import" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                                        )}
                                      </Box>
                                      <Box component="td" sx={{ textAlign: 'right', py: 1, fontFamily: 'monospace', color: isCounted ? 'text.primary' : 'text.disabled' }}>
                                        {formatMoney(d.ves_val_mo)}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>

                            {/* Time Series Chart */}
                            {htsShiftDetail.chart_data && htsShiftDetail.chart_data.length > 0 && (
                              <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Trade History (All Countries)
                                </Typography>
                                <Box sx={{ height: 280 }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={htsShiftDetail.chart_data}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                      <XAxis
                                        dataKey="month"
                                        tickFormatter={(m) => formatMonthLabel(m)}
                                        tick={{ fontSize: 10 }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={50}
                                      />
                                      <YAxis
                                        tickFormatter={(v) => formatMoney(v)}
                                        tick={{ fontSize: 10 }}
                                        width={60}
                                      />
                                      <RechartsTooltip
                                        formatter={(value, name) => [
                                          formatMoney(value as number | null),
                                          htsShiftDetail.chart_countries?.find(c => c.iso === name)?.name || name
                                        ]}
                                        labelFormatter={(label) => formatMonthLabel(label as string)}
                                        contentStyle={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                          border: '1px solid #bdbdbd',
                                          borderRadius: 4,
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                          fontSize: '0.75rem',
                                          maxHeight: 250,
                                          overflowY: 'auto',
                                        }}
                                        itemSorter={(item) => {
                                          // Selected country first, then sort by value
                                          if (item.dataKey === selectedHtsCountry) return -Infinity;
                                          return -(item.value as number || 0);
                                        }}
                                      />
                                      <ReferenceLine
                                        x={htsShiftDetail.pivot_month}
                                        stroke="#f44336"
                                        strokeDasharray="5 5"
                                        label={{ value: 'Pivot', position: 'top', fill: '#f44336', fontSize: 10 }}
                                      />
                                      {/* Render non-selected countries first, then selected on top */}
                                      {htsShiftDetail.chart_countries
                                        ?.slice()
                                        .sort((a, b) => {
                                          if (a.iso === selectedHtsCountry) return 1;
                                          if (b.iso === selectedHtsCountry) return -1;
                                          return 0;
                                        })
                                        .map((chartCountry, idx) => {
                                          const isSelected = chartCountry.iso === selectedHtsCountry;
                                          const colors = ['#90a4ae', '#b0bec5', '#78909c', '#607d8b', '#546e7a'];
                                          let color = colors[idx % colors.length];
                                          let strokeWidth = 1;
                                          let opacity = 0.4;

                                          if (isSelected) {
                                            const selCountry = htsShiftDetail.countries.find(c => c.iso === selectedHtsCountry);
                                            color = selCountry?.direction === 'DROP' ? '#d32f2f' : '#2e7d32';
                                            strokeWidth = 3;
                                            opacity = 1;
                                          }

                                          return (
                                            <Line
                                              key={chartCountry.iso}
                                              type="monotone"
                                              dataKey={chartCountry.iso}
                                              stroke={color}
                                              strokeWidth={strokeWidth}
                                              strokeOpacity={opacity}
                                              dot={isSelected ? { r: 3 } : false}
                                              connectNulls
                                            />
                                          );
                                        })}
                                    </LineChart>
                                  </ResponsiveContainer>
                                </Box>
                              </>
                            )}
                          </>
                        );
                      })() : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <Typography color="text.secondary">Select a country to view details</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Typography color="text.secondary">Failed to load detail data.</Typography>
                  </Box>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* Substitution Detail Dialog */}
        <Dialog
          open={!!selectedSubstitution}
          onClose={() => {
            setSelectedSubstitution(null);
            setSubstitutionDetail(null);
            setShowOnlySelectedCountries(false);
          }}
          maxWidth="xl"
          fullWidth
          disableScrollLock
          disableRestoreFocus
          sx={{ '& .MuiDialog-paper': { height: '85vh' } }}
        >
          {selectedSubstitution && (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                    {selectedSubstitution.hts_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSubstitution.hts_description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={selectedSubstitution.risk_level}
                      size="small"
                      color={getRiskChipColor(selectedSubstitution.risk_level)}
                    />
                    <Chip
                      label={selectedSubstitution.pattern_type?.replace('_', ' ')}
                      size="small"
                      color={getPatternChipColor(selectedSubstitution.pattern_type)}
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <IconButton onClick={() => { setSelectedSubstitution(null); setSubstitutionDetail(null); setShowOnlySelectedCountries(false); }} size="small">
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                {detailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : substitutionDetail ? (
                  <>
                    {/* Summary Stats */}
                    <Box sx={{ display: 'flex', gap: 4, mb: 3, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="overline" color="text.secondary">Pivot Month</Typography>
                        <Typography variant="h6">{formatMonthLabel(substitutionDetail.pivot_month)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="overline" color="text.secondary">Substitution Rate</Typography>
                        <Typography variant="h6">
                          {formatPercent(substitutionDetail.summary?.substitution_rate ? substitutionDetail.summary.substitution_rate * 100 : null)}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Two-column layout for countries */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                      {/* Losing Country */}
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingDown sx={{ color: 'error.main' }} />
                          {substitutionDetail.country_a.name || substitutionDetail.country_a.iso} (Losing)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Pre-Pivot Avg</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatMoney(substitutionDetail.summary?.country_a_pre_avg)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Post-Pivot Avg</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatMoney(substitutionDetail.summary?.country_a_post_avg)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Drop</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'error.main' }}>
                              -{formatMoney(substitutionDetail.summary?.country_a_drop)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Monthly Import Values</Typography>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <Box component="thead">
                            <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                              <Box component="th" sx={{ textAlign: 'left', py: 1, pr: 2 }}>Month</Box>
                              <Box component="th" sx={{ textAlign: 'right', py: 1 }}>Value</Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {substitutionDetail.country_a.monthly_data.map((d, idx) => {
                              const isCounted = d.is_counted !== false;
                              return (
                                <Box
                                  component="tr"
                                  key={idx}
                                  sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: !isCounted ? '#f5f5f5' : 'transparent',
                                    ...(d.time_period === substitutionDetail.pivot_month && {
                                      borderTop: '2px dashed',
                                      borderTopColor: 'text.secondary',
                                    }),
                                  }}
                                >
                                  <Box component="td" sx={{ py: 1, pr: 2, color: isCounted ? 'text.primary' : 'text.disabled' }}>
                                    {formatMonthLabel(d.time_period)}
                                    {d.time_period === substitutionDetail.pivot_month && (
                                      <Chip label="Pivot" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                                    )}
                                  </Box>
                                  <Box component="td" sx={{ textAlign: 'right', py: 1, fontFamily: 'monospace', color: isCounted ? 'text.primary' : 'text.disabled' }}>
                                    {formatMoney(d.ves_val_mo)}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>

                      {/* Gaining Country */}
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUp sx={{ color: 'success.main' }} />
                          {substitutionDetail.country_b.name || substitutionDetail.country_b.iso} (Gaining)
                          {substitutionDetail.summary?.pattern_type === 'NEW_SUPPLIER' && (
                            <Chip label="New Supplier" size="small" color="error" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Pre-Pivot Avg</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatMoney(substitutionDetail.summary?.country_b_pre_avg)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Post-Pivot Avg</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatMoney(substitutionDetail.summary?.country_b_post_avg)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Spike</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                              +{formatMoney(substitutionDetail.summary?.country_b_spike)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Monthly Import Values</Typography>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <Box component="thead">
                            <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                              <Box component="th" sx={{ textAlign: 'left', py: 1, pr: 2 }}>Month</Box>
                              <Box component="th" sx={{ textAlign: 'right', py: 1 }}>Value</Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {substitutionDetail.country_b.monthly_data.map((d, idx) => {
                              const isCounted = d.is_counted !== false;
                              return (
                                <Box
                                  component="tr"
                                  key={idx}
                                  sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: !isCounted ? '#f5f5f5' : 'transparent',
                                    ...(d.time_period === substitutionDetail.pivot_month && {
                                      borderTop: '2px dashed',
                                      borderTopColor: 'text.secondary',
                                    }),
                                  }}
                                >
                                  <Box component="td" sx={{ py: 1, pr: 2, color: isCounted ? 'text.primary' : 'text.disabled' }}>
                                    {formatMonthLabel(d.time_period)}
                                    {d.time_period === substitutionDetail.pivot_month && (
                                      <Chip label="Pivot" size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                                    )}
                                  {d.is_first_import && (
                                    <Chip label="First Import" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                                <Box component="td" sx={{ textAlign: 'right', py: 1, fontFamily: 'monospace', color: isCounted ? 'text.primary' : 'text.disabled' }}>
                                  {formatMoney(d.ves_val_mo)}
                                </Box>
                              </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Time Series Chart */}
                    {substitutionDetail.chart_data && substitutionDetail.chart_data.length > 0 && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Trade History {showOnlySelectedCountries ? '(Selected Countries Only)' : '(All Countries)'}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={showOnlySelectedCountries}
                                onChange={(e) => setShowOnlySelectedCountries(e.target.checked)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Show only selected
                              </Typography>
                            }
                            sx={{ mr: 0 }}
                          />
                        </Box>
                        <Box sx={{ height: 350 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={substitutionDetail.chart_data}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis
                                dataKey="month"
                                tickFormatter={(m) => formatMonthLabel(m)}
                                tick={{ fontSize: 11 }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                              />
                              <YAxis
                                tickFormatter={(v) => formatMoney(v)}
                                tick={{ fontSize: 11 }}
                                width={70}
                              />
                              <RechartsTooltip
                                formatter={(value, name) => [
                                  formatMoney(value as number | null),
                                  substitutionDetail.chart_countries?.find(c => c.iso === name)?.name || name
                                ]}
                                labelFormatter={(label) => formatMonthLabel(label as string)}
                                contentStyle={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                  border: '1px solid #bdbdbd',
                                  borderRadius: 4,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                }}
                                itemSorter={(item) => {
                                  // Put the two compared countries at top
                                  if (item.dataKey === substitutionDetail.country_a.iso) return -1000000000;
                                  if (item.dataKey === substitutionDetail.country_b.iso) return -999999999;
                                  return -(item.value as number || 0);
                                }}
                              />
                              <ReferenceLine
                                x={substitutionDetail.pivot_month}
                                stroke="#f44336"
                                strokeDasharray="5 5"
                                label={{ value: 'Pivot', position: 'top', fill: '#f44336', fontSize: 11 }}
                              />
                              {/* Render lines for each country - muted lines first, then highlighted on top */}
                              {!showOnlySelectedCountries && substitutionDetail.chart_countries
                                ?.filter(c => c.iso !== substitutionDetail.country_a.iso && c.iso !== substitutionDetail.country_b.iso)
                                .map((country) => (
                                  <Line
                                    key={country.iso}
                                    type="monotone"
                                    dataKey={country.iso}
                                    stroke="#e0e0e0"
                                    strokeWidth={1}
                                    dot={false}
                                    connectNulls
                                  />
                                ))}
                              {/* Highlighted countries on top */}
                              {substitutionDetail.chart_countries
                                ?.filter(c => c.iso === substitutionDetail.country_a.iso || c.iso === substitutionDetail.country_b.iso)
                                .map((country) => {
                                  const isLoser = country.iso === substitutionDetail.country_a.iso;
                                  return (
                                    <Line
                                      key={country.iso}
                                      type="monotone"
                                      dataKey={country.iso}
                                      stroke={isLoser ? '#d32f2f' : '#2e7d32'}
                                      strokeWidth={3}
                                      dot={{ r: 4 }}
                                      connectNulls
                                    />
                                  );
                                })}
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Red dashed line indicates the pivot month. The losing country is shown in red, gaining country in green.
                        </Typography>
                      </>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary">Failed to load detail data.</Typography>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
          </Box>{/* End Right Content Area */}
        </Box>{/* End Main Content Area with Sidebar */}
    </Layout>
  );
}